import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart, formatINR } from "@/lib/cart";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, shippingFee, total } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-[#0F0F0F] border-l border-white/10 text-[#F5F5F0] w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <SheetTitle className="font-heading text-2xl text-[#F5F5F0] tracking-wide">
            Your Selection
          </SheetTitle>
          <SheetDescription className="sr-only">
            Review items in your cart, adjust quantities, and proceed to checkout.
          </SheetDescription>
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A8A85]">
            {items.length === 0 ? "Empty" : `${items.length} blend${items.length > 1 ? "s" : ""}`}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center pt-12">
              <p className="font-heading text-3xl text-[#F5F5F0] mb-3">Nothing yet.</p>
              <p className="text-sm text-[#8A8A85] mb-8 max-w-xs">
                Begin with a single jar. Or commit to the full house collection.
              </p>
              <button
                data-testid="cart-empty-shop-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/shop");
                }}
                className="px-7 py-3 bg-[#D4AF37] text-[#0A0A0A] text-xs uppercase tracking-[0.25em] hover:bg-[#e6c258] transition-colors"
              >
                Discover Blends
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((it) => (
                <li
                  key={it.cartId}
                  data-testid={`cart-item-${it.cartId}`}
                  className="flex gap-4 pb-6 border-b border-white/5"
                >
                  <div className="w-20 h-24 bg-[#1A1A1A] overflow-hidden flex-shrink-0">
                    <img
                      src={it.image}
                      alt={it.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <div className="font-heading text-lg leading-tight">{it.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A85] mt-1">
                          {it.weight}
                        </div>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.cartId}`}
                        onClick={() => removeItem(it.cartId)}
                        className="text-[#8A8A85] hover:text-[#D4AF37] transition-colors"
                        aria-label="Remove"
                      >
                        <Trash2 size={14} strokeWidth={1.4} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center border border-white/10">
                        <button
                          data-testid={`cart-decrement-${it.cartId}`}
                          onClick={() => updateQty(it.cartId, it.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-[#F5F5F0] hover:text-[#D4AF37]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm">{it.quantity}</span>
                        <button
                          data-testid={`cart-increment-${it.cartId}`}
                          onClick={() => updateQty(it.cartId, it.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-[#F5F5F0] hover:text-[#D4AF37]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm tracking-wider">
                        {formatINR(it.price * it.quantity)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/5 px-6 py-6 space-y-3 bg-[#0A0A0A]">
            <div className="flex justify-between text-sm text-[#8A8A85]">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal" className="text-[#F5F5F0]">{formatINR(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-[#8A8A85]">
              <span>Shipping</span>
              <span className="text-[#F5F5F0]">
                {shippingFee === 0 ? "Complimentary" : formatINR(shippingFee)}
              </span>
            </div>
            <div className="flex justify-between font-heading text-xl pt-3 border-t border-white/5">
              <span>Total</span>
              <span data-testid="cart-total" className="text-[#D4AF37]">{formatINR(total)}</span>
            </div>
            <button
              data-testid="cart-checkout-btn"
              onClick={goCheckout}
              className="w-full mt-4 py-4 bg-[#D4AF37] text-[#0A0A0A] text-xs uppercase tracking-[0.3em] hover:bg-[#e6c258] transition-colors"
            >
              Proceed to Checkout
            </button>
            <p className="text-[11px] text-[#8A8A85] text-center pt-1">
              Free shipping on orders above ₹1,500
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
